import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Check, AlertTriangle, X } from "lucide-react"; // Import Lucid icons

interface Column {
  label: string;
  accessor: string;
  cellRenderer?: (row: any) => JSX.Element; // ✅ Optional
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc" | null;
}

enum classType {
  machingProfile = "maching_profile",
  users = "users",
  astroProfile = "astro_profile",
  rejectProfile = "reject_profile",
}

interface TableCardViewProps {
  data: any[];
  columns: Column[];
  view: "table" | "card";
  onSort?: (key: string) => void;
  sortConfig?: SortConfig | null;
  classtype?: string;
  onRowClick?: (row: any, classType: string) => void;
}

const TableCardView: React.FC<TableCardViewProps> = ({
  data,
  columns,
  view,
  sortConfig,
  onSort,
  classtype,
  onRowClick,
}) => {
  const navigate = useNavigate();

  const handleCopy = (e: any, text: any) => {
    e.preventDefault();
    if (text === undefined || text === null) return;
    const stringText = text.toString();
    navigator.clipboard.writeText(stringText);
    toast.success(`${stringText} - ✅ Copied!`);
  };
  // Card View
  if (view === "card") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(data || []).map((row, idx) => (
          <div
            key={idx}
            className="bg-white/10 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/10 transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,255,255,0.2)] hover:border-white/20 hover:bg-white/20"
          >
            <div className="space-y-2 text-xs">
              {columns.map((col) => (
                <div key={col.accessor}>
                  <span className="font-medium text-white/40">
                    {col.label}:
                  </span>{" "}
                  {col.accessor === "status" ? (
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                        row[col.accessor] === "Active"
                          ? "bg-green-200 text-green-900"
                          : "bg-red-200 text-red-900"
                      }`}
                    >
                      {row[col.accessor]}
                    </span>
                  ) : (
                    <span
                      className={` ${
                        row[col.accessor] === "yes"
                          ? "bg-green-200 text-green-900 px-3 py-1 text-xs font-semibold rounded-full"
                          : row[col.accessor] === "no"
                            ? "bg-red-200 text-red-900 px-3 py-1 text-xs font-semibold rounded-full"
                            : ""
                      }`}
                    >
                      {row[col.accessor]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!columns || !Array.isArray(columns)) {
    return (
      <div className="text-center text-gray-400 py-6">
        ⚠️ Columns not defined for this view.
      </div>
    );
  }
  if (!Array.isArray(data)) data = [];
  if (!Array.isArray(columns)) columns = [];
  // Table View
  return (
    <table className="min-w-full text-sm text-left text-slate-300 border-separate border-spacing-0">
      <thead className="sticky top-0 z-10">
        <tr className="bg-slate-950 text-[10px] uppercase font-bold tracking-widest text-slate-500">
            {(columns || []).map((col, idx) => {
              const isActive = sortConfig?.key === col.accessor;
              const icon = isActive
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "";
              return (
                <th
                  key={col.accessor}
                  className={`px-6 py-4 cursor-pointer select-none hover:text-[#ffcc00] transition-all border-b border-white/5 ${idx === 0 ? "rounded-tl-xl" : ""} ${idx === columns.length - 1 ? "rounded-tr-xl" : ""}`}
                  onClick={() => onSort?.(col.accessor)}
                  style={{ whiteSpace: "nowrap" }}
                >
                  <span className="flex items-center gap-2">
                    {col.label}
                    {isActive && (
                      <span className="text-[#ffcc00] transform animate-bounce-subtle">
                        {sortConfig.direction === "asc" ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3 h-3"
                          >
                            <path d="m18 15-6-6-6 6" />
                          </svg>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3 h-3"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        )}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="divide-y divide-white/[0.03]">
          {(!data || (Array.isArray(data) && data.length === 0)) && (
            <tr>
              <td
                colSpan={columns?.length || 1}
                className="py-12 text-center text-slate-500 font-medium italic"
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-3xl opacity-20">📭</span>
                  No data found in this category
                </div>
              </td>
            </tr>
          )}
          {data.length > 0 &&
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`group transition-all duration-200 ${
                  row.status?.toString()?.toLowerCase() === "inactive" ||
                  row.status?.toString()?.toLowerCase() === "no" ||
                  row.publish?.toString()?.toLowerCase() === "no"
                    ? "bg-rose-500/[0.05] hover:bg-rose-500/[0.1]"
                    : "hover:bg-white/[0.04]"
                } ${
                  classtype === classType.machingProfile ||
                  classtype === classType.astroProfile ||
                  classtype === classType.rejectProfile
                    ? "cursor-pointer"
                    : ""
                }`}
              >
                {columns.map((col) => (
                  <td
                    onContextMenu={(e: any) => {
                      if (row.candidate_id) {
                        handleCopy(e, row.candidate_id);
                      } else if (row.id) {
                        handleCopy(e, row.id);
                      } else if (row.userId) {
                        handleCopy(e, row.userId);
                      } else if (row.matrimonial_id) {
                        handleCopy(e, row.matrimonial_id);
                      }
                    }}
                    key={col.accessor}
                    className="px-6 py-4 max-w-[40vw] break-words relative hover:z-[50]"
                    onClick={() => {
                      if (classtype === classType.users) {
                        const userData = {
                          userId: row.id?.toString(),
                          name: row.name,
                          email: row.email,
                          phone: row.phone,
                          gender: row.gender,
                          age: row.age,
                          status: row.status,
                          progress: row.progress,
                          verified: row.verified,
                        };
                        localStorage.setItem(
                          "userDetails",
                          JSON.stringify(userData),
                        );
                        navigate(`/users/user/${row.id}`, {
                          state: {
                            from: classtype === "users" ? "users" : classtype,
                          },
                        });
                      } else if (
                        classtype === classType.machingProfile ||
                        classtype === classType.astroProfile ||
                        classtype === classType.rejectProfile
                      ) {
                        onRowClick?.(row, classtype);
                      }
                    }}
                  >
                    {col.cellRenderer ? (
                      <div className="relative">{col.cellRenderer(row)}</div>
                    ) : col.accessor === "status" ? (
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold tracking-tighter uppercase rounded-full border ${
                          row[col.accessor]?.toString()?.toLowerCase() ===
                            "active" ||
                          row[col.accessor]?.toString()?.toLowerCase() === "yes"
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                            : "bg-red-500/10 text-red-400 border-red-500/20"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${row[col.accessor]?.toString()?.toLowerCase() === "active" ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`}
                        ></span>
                        {row[col.accessor]}
                      </span>
                    ) : (
                      <span
                        title={row[col.accessor]}
                        className={`truncate block ${
                          row[col.accessor]?.toString()?.toLowerCase() === "yes"
                            ? "bg-emerald-500/10 text-emerald-400 px-3 py-1 text-[10px] font-bold uppercase rounded-full border border-emerald-500/20"
                            : row[col.accessor]?.toString()?.toLowerCase() ===
                                "no"
                              ? "bg-red-500/10 text-red-400 px-3 py-1 text-[10px] font-bold uppercase rounded-full border border-red-500/20"
                              : ""
                        }`}
                      >
                        {col.accessor === "icon" ? (
                          <div className="flex items-center gap-2">
                            {row[col.accessor] === "checked" && (
                              <Check className="text-emerald-500 w-4 h-4" />
                            )}
                            {row[col.accessor] === "caution" && (
                              <AlertTriangle className="text-amber-500 w-4 h-4" />
                            )}
                            {row[col.accessor] === "cross" && (
                              <X className="text-red-500 w-4 h-4" />
                            )}
                          </div>
                        ) : row[col.accessor]?.toString()?.length > 50 ? (
                          `${row[col.accessor]?.toString()?.substring(0, 50)}...`
                        ) : (
                          row[col.accessor]
                        )}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
    </table>
  );
};

export default TableCardView;
